import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Filter } from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { formatLisbon } from '../../utils/moderationFormat';

const PER_PAGE = 50;

const AuditPage = () => {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');
  const [photoIdFilter, setPhotoIdFilter] = useState('');
  const [fireIdFilter, setFireIdFilter] = useState('');

  const params = { page, per_page: PER_PAGE };
  if (action) params.action = action;
  if (photoIdFilter.trim()) params.photo_id = photoIdFilter.trim();
  if (fireIdFilter.trim()) params.fire_id = fireIdFilter.trim();

  const { data, isLoading } = useQuery(
    ['moderation-audit', params],
    () => apiService.getModerationAudit(params),
    { keepPreviousData: true },
  );

  if (isLoading) return <LoadingSpinner />;

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Auditoria</h1>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-gray-500" />
        <select
          value={action}
          onChange={(e) => { setAction(e.target.value); setPage(1); }}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">Todas as acções</option>
          <option value="approved">Aprovadas</option>
          <option value="rejected">Rejeitadas</option>
        </select>
        <input
          value={photoIdFilter}
          onChange={(e) => { setPhotoIdFilter(e.target.value); setPage(1); }}
          placeholder="photo_id"
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <input
          value={fireIdFilter}
          onChange={(e) => { setFireIdFilter(e.target.value); setPage(1); }}
          placeholder="fire_id"
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <span className="ml-auto text-sm text-gray-500">{total} entradas</span>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-left">
            <tr>
              <th className="px-3 py-2">Quando</th>
              <th className="px-3 py-2">Quem</th>
              <th className="px-3 py-2">Acção</th>
              <th className="px-3 py-2">Photo</th>
              <th className="px-3 py-2">Fire</th>
              <th className="px-3 py-2">Visibilidade</th>
              <th className="px-3 py-2">Assinatura</th>
              <th className="px-3 py-2">Razão</th>
              <th className="px-3 py-2">Flags</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-500">Sem registos</td></tr>
            )}
            {rows.map((r) => {
              // photo_public_at_decision is null on entries from before May 2026 — render as '—'.
              const consentPublic = r.photo_public_at_decision;
              return (
                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-3 py-2 whitespace-nowrap">{formatLisbon(r.decided_at)}</td>
                  <td className="px-3 py-2">{r.user ? `${r.user.name} ${r.user.surname}` : `#${r.user_id}`}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      r.action === 'approved'
                        ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200'
                        : 'bg-fire-100 text-fire-800 dark:bg-fire-900/40 dark:text-fire-200'
                    }`}>{r.action}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.photo_id}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.fire_id || '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    {consentPublic === null || consentPublic === undefined ? (
                      <span className="text-gray-500">—</span>
                    ) : consentPublic ? (
                      <span className="text-primary-700 dark:text-primary-300">Pública</span>
                    ) : (
                      <span className="text-purple-700 dark:text-purple-300">Operacional</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.photo_signature_at_decision || '—'}</td>
                  <td className="px-3 py-2">{r.reason || '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.flags_snapshot?.length
                      ? r.flags_snapshot.map((f) => f.code).join(', ')
                      : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-50 dark:border-gray-600"
          >
            Anterior
          </button>
          <span className="text-sm text-gray-500">{page} / {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-50 dark:border-gray-600"
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditPage;
